/*
 * Venenon — add-to-cart bridge.
 *
 * Reuses Horizon's cart architecture rather than building a second cart:
 *   - posts to Shopify's real cart endpoint (Theme.routes.cart_add_url)
 *   - dispatches the Shopify Standard `CartLinesUpdateEvent`, which Horizon's
 *     <cart-icon> and <cart-drawer-component> already listen for
 *   - refreshes the drawer through Horizon's <cart-items-component>
 *
 * localStorage is never used as a source of truth for the cart.
 */

import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const ERROR_DISPLAY_MS = 8000;

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

async function refreshCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
  }
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
  return response.json();
}

class VenenonProductForm extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    this.form = this.querySelector('form[data-ven-product-form]');
    this.errorEl = this.querySelector('[data-ven-form-error]');
    this.liveRegion = this.querySelector('[data-ven-live-region]');
    this.form?.addEventListener('submit', this.#onSubmit, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  get #buttons() {
    return [...this.querySelectorAll('[data-ven-add-to-cart]')];
  }

  #setBusy(busy) {
    this.#buttons.forEach((button) => {
      button.toggleAttribute('data-busy', busy);
      button.disabled = busy || button.dataset.soldOut === 'true';
    });
  }

  #announce(message) {
    if (this.liveRegion) this.liveRegion.textContent = message;
  }

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
    this.#announce(message);
    clearTimeout(this.errorTimeout);
    this.errorTimeout = setTimeout(() => {
      this.errorEl.hidden = true;
      this.#announce('');
    }, ERROR_DISPLAY_MS);
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    const formData = new FormData(this.form);
    const sections = cartSectionIds();
    if (sections) formData.append('sections', sections);

    const quantity = Number(formData.get('quantity')) || 1;
    const merchandiseId = String(formData.get('id') || '');
    if (!merchandiseId) {
      this.#showError(this.dataset.errorUnavailable || 'Please choose an available option.');
      return;
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId, quantity }],
        promise: deferred.promise,
      })
    );

    this.#setBusy(true);
    if (this.errorEl) this.errorEl.hidden = true;

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
        credentials: 'same-origin',
      });
      const payload = await response.json();

      if (payload.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: payload.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: payload.description, errors: payload.errors },
          })
        );
        this.#showError(payload.description || payload.message);
        const cart = await refreshCart().catch(() => null);
        deferred.resolve({
          cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
          detail: { didError: true, items: cart?.items, source: 'venenon-product-form', itemCount: quantity },
        });
        return;
      }

      this.#announce(this.dataset.addedText || '');
      const cart = await refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'venenon-product-form',
          sourceId: this.id,
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: payload.sections,
          didError: false,
        },
      });

      // Open Horizon's cart drawer when the theme is configured to use one.
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer && typeof drawer.open === 'function' && this.dataset.openDrawer !== 'false') {
        await customElements.whenDefined('theme-drawer');
        if (!drawer.isOpen) drawer.open();
      }
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' })
      );
      this.#showError(this.dataset.errorNetwork || 'Something went wrong. Please try again.');
    } finally {
      this.#setBusy(false);
    }
  };
}

if (!customElements.get('venenon-product-form')) {
  customElements.define('venenon-product-form', VenenonProductForm);
}

/*
 * Keeps the Venenon header cart badge in sync with the real cart. Listens to the
 * same Standard Event Horizon's own cart icon uses, so both stay consistent.
 */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ cart, detail }) => {
      const count = cart?.totalQuantity ?? detail?.itemCount;
      if (count == null) return;
      document.querySelectorAll('[data-ven-cart-count]').forEach((el) => {
        el.textContent = String(count);
        el.hidden = count === 0;
      });
      document.querySelectorAll('[data-ven-cart-count-text]').forEach((el) => {
        const label = el.textContent.split(':')[0];
        el.textContent = `${label}: ${count}`;
      });
    })
    .catch(() => {});
});
